import { planBooleanPathAttempts } from '@/adapters/primitive/_pure/derivation/boolean/algebra';
import { deriveNaturalLessThanTrace } from '@/adapters/primitive/_pure/model/natural/comparison';
import { deriveNaturalDivisionTrace } from '@/adapters/primitive/_pure/model/natural/division';
import { deriveNaturalTrace } from '@/adapters/primitive/_pure/model/natural/machine';
import { deriveNaturalMultiplicationTrace } from '@/adapters/primitive/_pure/model/natural/multiplication';
import type { PrimitiveOperationInput } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveTrace, PrimitiveTracePayload, PrimitiveTraceSettings } from '@/adapters/primitive/_contract/trace';

export function derivePrimitiveTracePayload(input: PrimitiveOperationInput, settings: PrimitiveTraceSettings): PrimitiveTracePayload {
    const options = {
        allowExoticTerminalStates: settings.allowExoticTerminalStates,
        runawayBorrowAssemblyBudget: settings.runawayBorrowAssemblyBudget,
        runawayDivisionCycleBudget: settings.runawayDivisionCycleBudget,
    };
    switch (input.operation) {
        case 'addition':
        case 'subtraction':
            return { kind: 'natural', settings, input: { ...input, operation: input.operation },
                semantic: deriveNaturalTrace({ ...input, radix: settings.radix,
                    operandSchedulingMode: settings.operandSchedulingMode }, options) };
        case 'multiplication':
            return { kind: 'multiplication', settings, input: { ...input, operation: input.operation },
                semantic: deriveNaturalMultiplicationTrace({ multiplier: input.operandOne,
                    multiplicand: input.operandTwo, radix: settings.radix }) };
        case 'division':
            return { kind: 'division', settings, input: { ...input, operation: input.operation },
                semantic: deriveNaturalDivisionTrace({ dividend: input.operandOne,
                    divisor: input.operandTwo, radix: settings.radix }, options) };
        case 'natural-less-than':
            return { kind: 'comparison', settings, input,
                semantic: deriveNaturalLessThanTrace({ ...input, radix: settings.radix }) };
        case 'natural-identity':
            return { kind: 'natural-identity', settings, input, semantic: { value: input.input } };
        case 'boolean-identity':
            return { kind: 'boolean-identity', settings, input, semantic: { value: input.input } };
        case 'logical-not':
            return { kind: 'boolean', settings, input,
                semantic: planBooleanPathAttempts({ operation: input.operation, inputs: [input.input] }) };
        default:
            return { kind: 'boolean', settings, input,
                semantic: planBooleanPathAttempts({ operation: input.operation, inputs: [input.operandOne, input.operandTwo] }) };
    }
}

export function generatePrimitiveTrace(args: {
    handle: string;
    title: string;
    input: PrimitiveOperationInput;
    settings: PrimitiveTraceSettings;
}): PrimitiveTrace {
    if (!args.handle.trim() || !args.title.trim()) throw new Error('Primitive trace requires a handle and title.');
    return { adapter: 'primitive-space', traceKind: 'PrimitiveTrace', schemaVersion: 1,
        handle: args.handle, title: args.title, payload: derivePrimitiveTracePayload(args.input, args.settings) };
}
